import { IconWallBase } from "$root/components/IconWall";
import { ProgressBar } from "@mdotm/shadcn/legacy/components";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { type JSX, useEffect, useState } from "react";
import { chartsComputing, LAB_COMPUTE_SLOW_MS } from "../../common";
import { PortfolioLabContext, type PortfolioLabInvestmentItem } from "../../ctx";

/** When the longest-waiting computing column was queued; `null` while none is computing. */
function oldestQueuedAt(investments: PortfolioLabInvestmentItem[]): number | null {
	const queuedAt = investments.flatMap((i) =>
		chartsComputing(i) && i.pendingCompute ? [i.pendingCompute.queuedAt] : [],
	);
	return queuedAt.length > 0 ? Math.min(...queuedAt) : null;
}

/**
 * The Lab charts' waiting wall: the shared "Calculating..." look, plus a second line once the
 * longest-waiting queued compute has run for {@link LAB_COMPUTE_SLOW_MS}. The line's space is
 * reserved from the start, so the wall does not shift when it appears.
 */
export function LabCalculatingWall(): JSX.Element {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const since = oldestQueuedAt(useAtomValue(ctx.investments$));
	const [now, setNow] = useState(Date.now);
	const slow = since != null && now - since >= LAB_COMPUTE_SLOW_MS;

	useEffect(() => {
		if (since == null || slow) {
			return;
		}
		const timer = setTimeout(() => setNow(Date.now()), since + LAB_COMPUTE_SLOW_MS - Date.now());
		return () => clearTimeout(timer);
	}, [since, slow]);

	return (
		<IconWallBase data-qualifier="PortfolioLab/CalculatingWall">
			<ProgressBar
				value="indeterminate"
				accentColor={themeCSSVars.palette_A300}
				backgroundColor={themeCSSVars.palette_N100}
				className="absolute top-0 inset-x-0"
				barHeight={8}
			/>
			<div className="flex flex-col">
				<h4 className="text-center">Calculating...</h4>
				<p
					data-qualifier="PortfolioLab/CalculatingWall/Slow"
					aria-hidden={!slow}
					className={toClassName({ "text-center": true, invisible: !slow })}
				>
					Taking longer than usual. The charts update as soon as the result arrives.
				</p>
			</div>
		</IconWallBase>
	);
}
